import { api } from '@/common/api';
import localReferences from '@/helpers/localReferences';

const state = {
    loadFromServer: false,
    isAuth: ((localStorage.getItem('user-auth') === '1')),
    token: localStorage.getItem('user-token') || '',
    name: '',
    email: '',
    officePhone: '',
    id: localStorage.getItem('user-id') || null,
    employerId: null,
    employer: {},
    availableDealOffices: {},
    roles: [],
    permissions: [],
    settings: {},
    yandexApiKey: '',
    daDataApiKey: ''
};

const getters = {
    isAuthenticated: state => !!state.token,
    dealPermissions: state => state.permissions.filter(item => item.includes('deal_')),
    sectionsFromPermissions: state => state.permissions.filter(permission => permission.includes('section_')).map(permission => permission.substr(8)),
    timesheetRoles: state => Object.keys(state.roles).filter(item => item.includes('timesheet_')),
    isCanEditCalendar: state => Object.keys(state.roles).includes('calendar_full_access'),
    isIncomeCashEmployer: state => Object.keys(state.roles).includes('incomes_cash_employer'),
    isIncomeCashDirector: state => Object.keys(state.roles).includes('incomes_cash_director'),
    isIncomeCashLessAccountant: state => Object.keys(state.roles).includes('incomes_cashless_accountant'),
    isIncomesCashLessEmployer: state => Object.keys(state.roles).includes('incomes_cashless_employer'),
    isCanEditMap: state => Object.keys(state.roles).includes('map_full_access'),
    isCanEditInstallationMap: state => Object.keys(state.roles).includes('installation_map_full_access'),
    isCanDealExportExcel: state => Object.keys(state.roles).includes('deal_export_excel'),
    isCanLeadExportExcel: (state) => {
        const allowedRoles = ['lead_full_access', 'lead_by_office', 'lead_responsible'];
        return allowedRoles.some((role) => Object.keys(state.roles).includes(role));
    },
    isExpensesAll: state => Object.keys(state.roles).includes('expenses_all'),
    isShowFiredEmployers: state => Object.keys(state.roles).includes('show_fired'),
    isCurrentEmployerDirector: state => [localReferences.employerPositions.ceo, localReferences.employerPositions.financeDirector].includes(state.employer.position_id),
    isCurrentEmployerManager: state => state.employer.position_id === localReferences.employerPositions.manager,
    isCurrentEmployerAccountant: state => state.employer.position_id === localReferences.employerPositions.accountant,
    isCurrentEmployerMeasurer: state => state.employer.position_id === localReferences.employerPositions.measurer,
    isCurrentEmployerInstaller: state => state.employer.position_id === localReferences.employerPositions.installer,
    isCurrentEmployerHeadInstallation: state => state.employer.position_id === localReferences.employerPositions.headInstallation,
    yandexApiKey: state => state.yandexApiKey,
    daDataApiKey: state => state.daDataApiKey
};

const actions = {
    login(context, credentials) {
        return new Promise((resolve, reject) => {
            api.post('/login', credentials).then(response => {
                this.commit('setToken', response.data.data.token);
                resolve(true);
            }).catch(
                error => {
                    reject(error);
                });
        });
    },
    logout({ commit }) {
        return new Promise((resolve, reject) => {
            api.defaults.headers.common.Authorization = state.token;
            api.get('/logout').then(response => {
                commit('clearToken');
                resolve(response);
            }).catch(
                error => {
                    reject(error);
                });
        });
    },
    async getAuth({ state, commit }) {
        try {
            const token = localStorage.getItem('user-token');
            if (token) {
                api.defaults.headers.common.Authorization = token;
                const response = await api.get('/auth');
                commit('setAuthInfo', response.data.data);
            }
        } catch (e) {
            commit('clearToken');
            throw new Error(e);
        }
    },
    async auth({ commit }) {
        const token = localStorage.getItem('user-token');
        return new Promise((resolve, reject) => {
            if (token) {
                api.defaults.headers.common.Authorization = token;
                api.get('/auth').then(response => {
                    commit('setAuthInfo', response.data.data);
                    resolve(response);
                }).catch(
                    error => {
                        commit('clearToken');
                        reject(error);
                    });
            } else {
                reject(new Error('Unauthorized Error'));
            }
        });
    }
};

const mutations = {
    setToken(state, token) {
        token = 'Bearer ' + token;
        state.token = token;
        state.isAuth = true;
        localStorage.setItem('user-token', token);
        api.defaults.headers.common.Authorization = token;
    },
    clearToken(state) {
        state.token = '';
        state.isAuth = false;
        localStorage.removeItem('user-token');
        localStorage.removeItem('user-name');
        localStorage.removeItem('user-email');
        localStorage.removeItem('user-roles');
        localStorage.removeItem('user-auth');
        delete api.defaults.headers.common.Authorization;
    },
    setAuthInfo(state, data) {
        state.token = localStorage.getItem('user-token') || '';
        state.name = data.name;
        state.email = data.email;
        state.officePhone = data.officePhone;
        state.id = data.id;
        state.employerId = data.employerId;
        state.roles = data.roles;
        state.permissions = data.permissions;
        state.settings = data.settings;
        state.yandexApiKey = data.yandexApiKey;
        state.daDataApiKey = data.daDataApiKey;
        state.employer = data.employer;
        state.availableDealOffices = data.availableDealOffices;
        state.isAuth = true;
        state.loadFromServer = true;
        localStorage.setItem('user-id', data.id);
    }
};

export default {
    state,
    getters,
    actions,
    mutations
};
